// Period presets for the P&L view. Dates are local-time YYYY-MM-DD strings,
// matching what <input type="date"> produces and the API accepts.

export interface Period {
  from: string
  to: string
}

export const PERIOD_PRESETS = [
  'this_month',
  'last_month',
  'this_quarter',
  'this_year',
  'last_12_months',
  'custom',
] as const

export type PeriodPreset = (typeof PERIOD_PRESETS)[number]

export const PERIOD_PRESET_LABELS: Record<PeriodPreset, string> = {
  this_month: 'This month',
  last_month: 'Last month',
  this_quarter: 'This quarter',
  this_year: 'This year',
  last_12_months: 'Last 12 months',
  custom: 'Custom',
}

const toISO = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

export const today = () => toISO(new Date())

export function presetPeriod(preset: PeriodPreset): Period {
  const now = new Date()
  const y = now.getFullYear()
  const m = now.getMonth()

  switch (preset) {
    case 'this_month':
      return { from: toISO(new Date(y, m, 1)), to: toISO(new Date(y, m + 1, 0)) }
    case 'last_month':
      return { from: toISO(new Date(y, m - 1, 1)), to: toISO(new Date(y, m, 0)) }
    case 'this_quarter': {
      const qStart = new Date(y, Math.floor(m / 3) * 3, 1)
      return { from: toISO(qStart), to: toISO(new Date(y, m + 1, 0)) }
    }
    case 'this_year':
      return { from: toISO(new Date(y, 0, 1)), to: toISO(now) }
    case 'last_12_months':
      return { from: toISO(new Date(y, m - 11, 1)), to: toISO(now) }
    case 'custom':
      return { from: toISO(new Date(y, m, 1)), to: toISO(now) }
  }
}

// The comparable period immediately before the given one: same length,
// ending the day before `from`. For calendar presets (month/quarter) this
// lands exactly on the previous month/quarter.
export function priorPeriod(p: Period): Period {
  const from = new Date(`${p.from}T00:00:00`)
  const to = new Date(`${p.to}T00:00:00`)
  const days = Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1
  const priorTo = new Date(from)
  priorTo.setDate(priorTo.getDate() - 1)
  const priorFrom = new Date(priorTo)
  priorFrom.setDate(priorFrom.getDate() - days + 1)
  return { from: toISO(priorFrom), to: toISO(priorTo) }
}

export function periodLabel(p: Period): string {
  return `${p.from} → ${p.to}`
}
